<!--
  OrderHourlyPaymentView.vue — 小时统计
  按小时 + 应用维度展示收款、退款、首购、续费统计。
  功能：日期筛选、应用筛选、趋势折线图、分页明细。
  接口：GET /v1/analytics/order/stats/hourly/payment
-->
<template>
  <div class="hourly-stat-page">
    <a-card :bordered="false" style="margin-bottom: 16px">
      <a-form layout="inline" :model="searchForm">
        <a-form-item label="开始日期">
          <a-date-picker v-model:value="searchForm.startDate" value-format="YYYY-MM-DD" :allow-clear="true"
            placeholder="开始日期" style="width: 140px" @change="clearQuickDate" />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker v-model:value="searchForm.endDate" value-format="YYYY-MM-DD" :allow-clear="true"
            placeholder="结束日期" style="width: 140px" @change="clearQuickDate" />
        </a-form-item>
        <a-form-item label="快捷日期">
          <a-radio-group v-model:value="quickDateValue" option-type="button" button-style="solid"
            class="quick-date-group" @change="handleQuickDateChange">
            <a-radio-button v-for="item in quickDateOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="应用">
          <a-select v-model:value="searchForm.appId" style="width: 320px" placeholder="全部应用" allow-clear show-search
            option-filter-prop="label" :options="appOptions" @change="handleAppChange" />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" :loading="loading || chartLoading" @click="handleSearch">查询</a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card :bordered="false" title="小时订单趋势" style="margin-bottom: 16px">
      <a-spin :spinning="chartLoading">
        <template v-if="chartPoints.length">
          <v-chart :option="chartOption" autoresize style="height: 360px" />
          <div class="chart-summary" v-if="latestPoint && totalPoint">
            <div class="summary-column">
              <div class="summary-title">最近1小时</div>
              <div class="summary-metrics">
                <span v-for="metric in summaryMetrics" :key="metric.key">
                  {{ metric.label }}：<b :class="metric.className">{{ formatCount(latestPoint[metric.key]) }}</b> 单
                </span>
              </div>
            </div>
            <div class="summary-column">
              <div class="summary-title">合计</div>
              <div class="summary-metrics">
                <span v-for="metric in summaryMetrics" :key="metric.key">
                  {{ metric.label }}：<b :class="metric.className">{{ formatCount(totalPoint[metric.key]) }}</b> 单
                </span>
              </div>
            </div>
            <div v-for="point in dailySummaryPoints" :key="point.statDate" class="summary-column">
              <div class="summary-title">{{ point.label }}</div>
              <div class="summary-metrics">
                <span v-for="metric in summaryMetrics" :key="metric.key">
                  {{ metric.label }}：<b :class="metric.className">{{ formatCount(point[metric.key]) }}</b> 单
                </span>
              </div>
            </div>
          </div>
        </template>
        <a-empty v-else description="暂无小时统计数据" class="chart-empty" />
      </a-spin>
    </a-card>

    <a-card :bordered="false" title="小时统计明细">
      <a-table :columns="columns" :data-source="tableData" :loading="loading" :pagination="pagination" row-key="rowKey"
        :scroll="{ x: 'max-content' }" size="small" bordered @change="handleTableChange" />
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
} from "echarts/components";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
]);

const APP_LIST = [
  {
    "id": 263,
    "name": "AI燃剪"
  },
  {
    "id": 267,
    "name": "权益卡-HL"
  },
  {
    "id": 291,
    "name": "英特布尔-wy合利宝-聚龙新通高价"
  },
  {
    "id": 290,
    "name": "英特布尔-wj合利宝-新通高价"
  },
  {
    "id": 289,
    "name": "英特布尔-cj合利宝-新通高价"
  },
  {
    "id": 288,
    "name": "英特布尔-wy合利宝-新通高价"
  },
  {
    "id": 287,
    "name": "英特布尔-wj合利宝-视界高价"
  },
  {
    "id": 286,
    "name": "英特布尔-cj合利宝-视界高价"
  },
  {
    "id": 285,
    "name": "英特布尔-wy合利宝-视界高价"
  },
  {
    id: 283,
    name: "英特布尔-快钱aimax-停车场",
  },
  {
    id: 282,
    name: "英特布尔-互动广告-快钱高价",
  },
  {
    id: 281,
    name: "英特布尔-zy合利宝-高价",
  },
  {
    id: 280,
    name: "英特布尔-wj快钱-高价",
  },
  {
    id: 279,
    name: "权益卡-云海-广点通",
  },
  {
    id: 278,
    name: "英特布尔-快钱-停车场",
  },
  {
    id: 277,
    name: "英特布尔-合利宝-高价",
  },
  {
    id: 276,
    name: "ali单次-承接",
  },
  {
    id: 275,
    name: "AI视频创作-大有九上",
  },
  {
    id: 274,
    name: "小歪-自投-合利宝",
  },
  {
    id: 273,
    name: "英特布尔-快钱-单双",
  },
  {
    id: 272,
    name: "英特布尔-快钱-单单",
  },
  {
    id: 271,
    name: "云海-玉笙线下",
  },
  {
    id: 270,
    name: "云海-合利宝-尾量",
  },
  {
    id: 269,
    name: "英特布尔-合利宝-停车场",
  },
  {
    id: 268,
    name: "权益卡-云海-合利宝",
  },
];

const appOptions = APP_LIST.map((app) => ({
  label: `${app.name} (${app.id})`,
  value: app.id,
}));

const quickDateOptions = [
  { label: "今天", value: "today", startOffset: 0, endOffset: 0 },
  { label: "昨天", value: "yesterday", startOffset: 1, endOffset: 1 },
  { label: "最近2天", value: "last_2_days", startOffset: 1, endOffset: 0 },
  { label: "最近3天", value: "last_3_days", startOffset: 2, endOffset: 0 },
  { label: "最近5天", value: "last_5_days", startOffset: 4, endOffset: 0 },
  { label: "最近7天", value: "last_7_days", startOffset: 6, endOffset: 0 },
  { label: "最近30天", value: "last_30_days", startOffset: 29, endOffset: 0 },
];

const summaryMetrics = [
  { key: "payOrderCount", label: "收款单数", className: "pay-color" },
  { key: "refundOrderCount", label: "退款单数", className: "refund-color" },
  { key: "firstPayOrderCount", label: "首购单数", className: "first-pay-color" },
  { key: "renewOrderCount", label: "续费单数", className: "renew-color" },
];

const loading = ref(false);
const chartLoading = ref(false);
const tableData = ref([]);
const chartRows = ref([]);
const dailySummaryRows = ref([]);
const quickDateValue = ref(null);

const searchForm = reactive({
  startDate: "",
  endDate: "",
  appId: undefined,
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const nowrap = { style: { whiteSpace: "nowrap" } };
const numberCell = {
  style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
};

const toNumber = (value) => Number(value || 0);

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysAgoDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDate(date);
};

const formatCount = (value) =>
  value === null || value === undefined
    ? "-"
    : Number(value).toLocaleString("zh-CN");

const formatHourLabel = (value) => {
  const s = String(value || "");
  if (s.length >= 13) return `${s.slice(5, 10)} ${s.slice(11, 13)}:00`;
  return s || "-";
};

const formatDayAxisLabel = (value) => {
  const s = String(value || "");
  if (s.length >= 10) return s.slice(5, 10);
  return s || "-";
};

const formatSummaryDayLabel = (value) => {
  const s = String(value || "");
  if (s.length >= 10) {
    return `${Number(s.slice(5, 7))}.${Number(s.slice(8, 10))}`;
  }
  return s || "-";
};

const formatHourAxisLabel = (value) => {
  const s = String(value || "");
  const timePart = s.split(" ")[1];
  if (timePart) return timePart.slice(0, 2);
  if (s.length >= 13) return s.slice(11, 13);
  return s || "-";
};

const addDaysToDateString = (dateString, days) => {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);
  return formatDate(date);
};

const buildDailySummaryParams = () => {
  if (!searchForm.startDate && !searchForm.endDate) return null;

  let startDate = searchForm.startDate;
  let endDate = searchForm.endDate;

  if (startDate && !endDate) {
    endDate = addDaysToDateString(startDate, 6);
  } else if (!startDate && endDate) {
    startDate = addDaysToDateString(endDate, -6);
  } else {
    const firstSevenDaysEnd = addDaysToDateString(startDate, 6);
    if (endDate > firstSevenDaysEnd) {
      endDate = firstSevenDaysEnd;
    }
  }

  const params = {
    startDate,
    endDate,
  };
  if (searchForm.appId !== null && searchForm.appId !== undefined) {
    params.appId = searchForm.appId;
  }
  return params;
};

const buildDayAxisMeta = (points) => {
  const dayBoundaryLabels = Array(points.length).fill("");
  const dayCenterLabels = Array(points.length).fill("");
  const daySegments = [];
  let startIndex = 0;

  for (let index = 1; index <= points.length; index += 1) {
    const isDayEnd =
      index === points.length ||
      points[index].dayLabel !== points[startIndex].dayLabel;
    if (!isDayEnd) continue;

    daySegments.push({
      label: points[startIndex].dayLabel,
      startIndex,
      centerIndex: Math.round((startIndex + index - 1) / 2),
    });
    startIndex = index;
  }

  const dateLabelStep =
    daySegments.length <= 7 ? 1 : Math.ceil(daySegments.length / 8);

  daySegments.forEach((segment, index) => {
    const isFirst = index === 0;
    const isLast = index === daySegments.length - 1;
    const shouldShowDate =
      isFirst || isLast || index % dateLabelStep === 0;

    dayBoundaryLabels[segment.startIndex] = segment.label;
    if (shouldShowDate) {
      dayCenterLabels[segment.centerIndex] = segment.label;
    }
  });

  return { dayBoundaryLabels, dayCenterLabels };
};

const getHourKey = (row) => {
  if (row.hourStart) return row.hourStart;
  if (row.statTime) return row.statTime;
  if (row.statDate && row.statHour !== undefined) {
    return `${row.statDate} ${String(row.statHour).padStart(2, "0")}:00:00`;
  }
  return "";
};

const getHourRange = (record) => {
  if (record.hourStart && record.hourEnd) {
    return `${formatHourLabel(record.hourStart)} - ${String(
      record.hourEnd
    ).slice(11, 16)}`;
  }
  return formatHourLabel(record.statTime);
};

const getDayKey = (row) => {
  if (row.statDate) return row.statDate;
  const key = getHourKey(row);
  if (key.length >= 10) return key.slice(0, 10);
  return "";
};

const buildParams = (pageIdx, pageSize) => {
  const params = {
    pageIdx,
    pageSize,
  };
  if (searchForm.startDate) params.startDate = searchForm.startDate;
  if (searchForm.endDate) params.endDate = searchForm.endDate;
  if (searchForm.appId !== null && searchForm.appId !== undefined) {
    params.appId = searchForm.appId;
  }
  return params;
};

const hasDateRange = () => Boolean(searchForm.startDate || searchForm.endDate);

const validateSearch = () => {
  if (
    searchForm.startDate &&
    searchForm.endDate &&
    searchForm.startDate > searchForm.endDate
  ) {
    message.warning("开始日期不能晚于结束日期");
    return false;
  }
  return true;
};

const normalizeRows = (rows) =>
  (rows || []).map((row) => ({
    ...row,
    rowKey: `${getHourKey(row)}_${row.appId}`,
  }));

const chartPoints = computed(() => {
  const map = new Map();
  for (const row of chartRows.value) {
    const key = getHourKey(row);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: formatHourLabel(key),
        dayLabel: formatDayAxisLabel(key),
        payOrderCount: 0,
        refundOrderCount: 0,
        firstPayOrderCount: 0,
        renewOrderCount: 0,
      });
    }
    const point = map.get(key);
    point.payOrderCount += toNumber(row.payOrderCount);
    point.refundOrderCount += toNumber(row.refundOrderCount);
    point.firstPayOrderCount += toNumber(row.firstPayOrderCount);
    point.renewOrderCount += toNumber(row.renewOrderCount);
  }
  return Array.from(map.values())
    .sort((a, b) => a.key.localeCompare(b.key));
});

const latestPoint = computed(
  () => chartPoints.value[chartPoints.value.length - 1] || null
);

const totalPoint = computed(() =>
  chartPoints.value.reduce(
    (sum, point) => {
      sum.payOrderCount += point.payOrderCount;
      sum.refundOrderCount += point.refundOrderCount;
      sum.firstPayOrderCount += point.firstPayOrderCount;
      sum.renewOrderCount += point.renewOrderCount;
      return sum;
    },
    {
      payOrderCount: 0,
      refundOrderCount: 0,
      firstPayOrderCount: 0,
      renewOrderCount: 0,
    }
  )
);

const dailySummaryPoints = computed(() => {
  const map = new Map();
  for (const row of dailySummaryRows.value) {
    const statDate = getDayKey(row);
    if (!statDate) continue;
    if (!map.has(statDate)) {
      map.set(statDate, {
        statDate,
        label: formatSummaryDayLabel(statDate),
        payOrderCount: 0,
        refundOrderCount: 0,
        firstPayOrderCount: 0,
        renewOrderCount: 0,
      });
    }
    const point = map.get(statDate);
    point.payOrderCount += toNumber(row.payOrderCount);
    point.refundOrderCount += toNumber(row.refundOrderCount);
    point.firstPayOrderCount += toNumber(row.firstPayOrderCount);
    point.renewOrderCount += toNumber(row.renewOrderCount);
  }
  return Array.from(map.values())
    .sort((a, b) => a.statDate.localeCompare(b.statDate))
    .slice(0, 7);
});

const average = (values) => {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const buildCountMarkLine = (value, color, position) => ({
  silent: true,
  symbol: "none",
  lineStyle: { type: "dashed", width: 1, color },
  label: {
    position,
    fontSize: 10,
    formatter: () => `均值 ${formatCount(Math.round(value))} 单`,
  },
  data: [{ yAxis: value }],
});

const chartOption = computed(() => {
  const labels = chartPoints.value.map((point) => point.label);
  const { dayBoundaryLabels, dayCenterLabels } = buildDayAxisMeta(
    chartPoints.value
  );
  const payCountData = chartPoints.value.map((point) => point.payOrderCount);
  const refundCountData = chartPoints.value.map(
    (point) => point.refundOrderCount
  );
  const firstPayCountData = chartPoints.value.map(
    (point) => point.firstPayOrderCount
  );
  const renewCountData = chartPoints.value.map(
    (point) => point.renewOrderCount
  );

  return {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      formatter: (params) =>
        params[0].axisValue +
        params
          .map(
            (item) =>
              `<br/>${item.marker}${item.seriesName}：${formatCount(
                item.value
              )} 单`
          )
          .join(""),
    },
    legend: {
      data: ["收款单数", "退款单数", "首购单数", "续费单数"],
      top: 0,
    },
    grid: { top: 42, left: 0, right: 8, bottom: 64, containLabel: true },
    xAxis: [
      {
        type: "category",
        data: labels,
        axisLine: { lineStyle: { color: "#d9d9d9" } },
        axisTick: {
          alignWithLabel: true,
          interval: 0,
          length: 4,
          lineStyle: { color: "#d9d9d9" },
        },
        axisLabel: {
          fontSize: 10,
          interval: 0,
          hideOverlap: false,
          showMinLabel: true,
          showMaxLabel: true,
          formatter: (value) => formatHourAxisLabel(value),
        },
      },
      {
        type: "category",
        data: labels,
        position: "bottom",
        offset: 26,
        axisLine: { show: false },
        axisTick: {
          alignWithLabel: true,
          interval: (index) => Boolean(dayCenterLabels[index]),
          length: 12,
          lineStyle: { color: "#8c8c8c", width: 1 },
        },
        axisLabel: {
          color: "#595959",
          fontSize: 11,
          interval: (index) => Boolean(dayCenterLabels[index]),
          margin: 8,
          formatter: (value, index) => dayCenterLabels[index] || "",
        },
        splitLine: {
          show: true,
          interval: (index) => Boolean(dayBoundaryLabels[index]),
          lineStyle: { color: "#f0f0f0", type: "dashed" },
        },
      },
    ],
    yAxis: {
      type: "value",
      name: "订单数",
      axisLabel: {
        fontSize: 11,
        formatter: (value) => Number(value).toLocaleString("zh-CN"),
      },
    },
    series: [
      {
        name: "收款单数",
        type: "line",
        smooth: true,
        showSymbol: false,
        data: payCountData,
        itemStyle: { color: "#1677ff" },
        markLine: buildCountMarkLine(
          average(payCountData),
          "#1677ff",
          "insideEndTop"
        ),
      },
      {
        name: "退款单数",
        type: "line",
        smooth: true,
        showSymbol: false,
        data: refundCountData,
        itemStyle: { color: "#ff4d4f" },
        markLine: buildCountMarkLine(
          average(refundCountData),
          "#ff4d4f",
          "insideEndBottom"
        ),
      },
      {
        name: "首购单数",
        type: "line",
        smooth: true,
        showSymbol: false,
        data: firstPayCountData,
        itemStyle: { color: "#722ed1" },
      },
      {
        name: "续费单数",
        type: "line",
        smooth: true,
        showSymbol: false,
        data: renewCountData,
        itemStyle: { color: "#13c2c2" },
        lineStyle: { type: "dashed" },
      },
    ],
  };
});

const columns = [
  {
    title: "小时区间",
    key: "hourRange",
    width: 150,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ record }) => getHourRange(record),
  },
  {
    title: "应用名称",
    dataIndex: "appName",
    key: "appName",
    width: 220,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "收款单数",
    dataIndex: "payOrderCount",
    key: "payOrderCount",
    width: 100,
    align: "center",
    customCell: () => numberCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatCount(text),
  },
  {
    title: "退款单数",
    dataIndex: "refundOrderCount",
    key: "refundOrderCount",
    width: 100,
    align: "center",
    customCell: () => numberCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatCount(text),
  },
  {
    title: "首购单数",
    dataIndex: "firstPayOrderCount",
    key: "firstPayOrderCount",
    width: 100,
    align: "center",
    customCell: () => numberCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatCount(text),
  },
  {
    title: "续费单数",
    dataIndex: "renewOrderCount",
    key: "renewOrderCount",
    width: 100,
    align: "center",
    customCell: () => numberCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatCount(text),
  },
  {
    title: "更新时间",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 165,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
];

const fetchTable = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const json = await get(
      analyticsUrl("/order/stats/hourly/payment"),
      buildParams(pageIdx, pageSize)
    );
    if (json.code === 200 && json.data) {
      const data = json.data;
      tableData.value = normalizeRows(data.data);
      pagination.total = data.dataSum ?? data.total ?? 0;
      pagination.current = data.pageIdx ?? pageIdx;
      pagination.pageSize = data.pageSize ?? pageSize;
    } else {
      message.warning("查询失败：" + (json.content || "未知错误"));
    }
  } catch {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

const fetchHourlyRows = async (params, pageSize = 1000) => {
  const first = await get(analyticsUrl("/order/stats/hourly/payment"), {
    ...params,
    pageIdx: 1,
    pageSize,
  });
  if (first.code !== 200 || !first.data) return [];

  const rows = [...(first.data.data || [])];
  const pageCount = first.data.pageCount ?? 1;
  if (pageCount <= 1) return rows;

  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      get(analyticsUrl("/order/stats/hourly/payment"), {
        ...params,
        pageIdx: index + 2,
        pageSize,
      })
    )
  );
  rest.forEach((json) => {
    if (json.code === 200 && json.data?.data) {
      rows.push(...json.data.data);
    }
  });
  return rows;
};

const fetchChart = async () => {
  chartLoading.value = true;
  try {
    const dailySummaryParams = buildDailySummaryParams();
    if (hasDateRange()) {
      chartRows.value = await fetchHourlyRows(buildParams(1, 1000));
      dailySummaryRows.value = dailySummaryParams
        ? await fetchHourlyRows(dailySummaryParams)
        : chartRows.value;
      return;
    }

    const json = await get(
      analyticsUrl("/order/stats/hourly/payment"),
      buildParams(1, 500)
    );
    if (json.code !== 200 || !json.data) {
      message.warning("趋势数据查询失败：" + (json.content || "未知错误"));
      return;
    }

    const rows = json.data.data || [];
    chartRows.value = rows;
    dailySummaryRows.value = dailySummaryParams
      ? await fetchHourlyRows(dailySummaryParams)
      : rows;
  } catch {
    chartRows.value = [];
    dailySummaryRows.value = [];
    message.error("趋势数据接口请求失败");
  } finally {
    chartLoading.value = false;
  }
};

const fetchData = async (pageIdx = 1, pageSize = 20, refreshChart = true) => {
  if (refreshChart) {
    await Promise.all([fetchTable(pageIdx, pageSize), fetchChart()]);
  } else {
    await fetchTable(pageIdx, pageSize);
  }
};

const handleSearch = () => {
  if (!validateSearch()) return;
  fetchData(1, pagination.pageSize);
};

const handleAppChange = () => {
  handleSearch();
};

const clearQuickDate = () => {
  quickDateValue.value = null;
};

const handleQuickDateChange = ({ target }) => {
  const option = quickDateOptions.find((item) => item.value === target.value);
  if (!option) return;
  searchForm.startDate = getDaysAgoDate(option.startOffset);
  searchForm.endDate = getDaysAgoDate(option.endOffset);
  handleSearch();
};

const handleReset = () => {
  searchForm.startDate = "";
  searchForm.endDate = "";
  searchForm.appId = undefined;
  quickDateValue.value = null;
  fetchData(1, pagination.pageSize);
};

const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize, false);
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.hourly-stat-page :deep(.ant-card-head-title) {
  min-height: 24px;
}

.quick-date-group {
  white-space: nowrap;
}

.chart-summary {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
  max-width: 100%;
  margin-top: 12px;
  padding-top: 12px;
  padding-bottom: 4px;
  overflow-x: auto;
  border-top: 1px solid #f0f0f0;
  color: #666;
  font-size: 12px;
  line-height: 1.6;
}

.summary-column {
  flex: 0 0 auto;
  min-width: max-content;
}

.summary-title {
  margin-bottom: 6px;
  color: #262626;
  font-size: 13px;
  font-weight: 600;
}

.summary-metrics {
  display: grid;
  grid-template-columns: max-content;
  gap: 2px;
}

.summary-metrics span {
  white-space: nowrap;
}

.chart-summary b {
  font-weight: 600;
}

.pay-color {
  color: #1677ff;
}

.refund-color {
  color: #ff4d4f;
}

.first-pay-color {
  color: #722ed1;
}

.renew-color {
  color: #13c2c2;
}

.chart-empty {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .chart-summary {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .summary-column {
    min-width: 0;
  }
}
</style>
